import { Edit2, MessageSquare, Send, ThumbsUp, Trash2, X } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import {
  useCreateAnswer,
  useCreateQuestion,
  useDeleteAnswer,
  useDeleteQuestion,
  useLessonQuestions,
  useToggleUpvote,
  useUpdateAnswer,
  useUpdateQuestion,
} from '@/hooks/use-questions'
import { useAuth } from '@/lib/auth'

interface QuestionPanelProps {
  lessonId: string
}

export function QuestionPanel({ lessonId }: QuestionPanelProps) {
  const { user } = useAuth()
  const { data, isLoading } = useLessonQuestions(lessonId)
  const createQuestion = useCreateQuestion()
  const toggleUpvote = useToggleUpvote()
  const createAnswer = useCreateAnswer()
  const updateQuestion = useUpdateQuestion()
  const deleteQuestion = useDeleteQuestion()
  const updateAnswer = useUpdateAnswer()
  const deleteAnswer = useDeleteAnswer()

  const [showForm, setShowForm] = useState(false)
  const [questionText, setQuestionText] = useState('')
  const [answerText, setAnswerText] = useState<Record<string, string>>({})

  // 編集中の質問/回答を管理
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null)
  const [editingQuestionText, setEditingQuestionText] = useState('')
  const [editingAnswerId, setEditingAnswerId] = useState<string | null>(null)
  const [editingAnswerText, setEditingAnswerText] = useState('')

  const questions = (data as any) ?? []

  const handleSubmitQuestion = async () => {
    if (!questionText.trim()) return
    try {
      await createQuestion.mutateAsync({
        lessonId,
        data: { question: questionText.trim() },
      })
      setQuestionText('')
      setShowForm(false)
      toast.success('質問を投稿しました')
    } catch {
      toast.error('投稿に失敗しました')
    }
  }

  const handleUpvote = async (questionId: string) => {
    try {
      await toggleUpvote.mutateAsync({ lessonId, questionId })
    } catch {
      toast.error('操作に失敗しました')
    }
  }

  const handleSubmitAnswer = async (questionId: string) => {
    const text = answerText[questionId]?.trim()
    if (!text) return
    try {
      await createAnswer.mutateAsync({
        lessonId,
        questionId,
        data: { answer: text },
      })
      setAnswerText((prev) => ({ ...prev, [questionId]: '' }))
      toast.success('回答を投稿しました')
    } catch {
      toast.error('投稿に失敗しました')
    }
  }

  // 質問の編集
  const handleStartEditQuestion = (questionId: string, currentText: string) => {
    setEditingQuestionId(questionId)
    setEditingQuestionText(currentText)
  }

  const handleSaveEditQuestion = async (questionId: string) => {
    if (!editingQuestionText.trim()) return
    try {
      await updateQuestion.mutateAsync({
        lessonId,
        questionId,
        data: { question: editingQuestionText.trim() },
      })
      setEditingQuestionId(null)
      toast.success('質問を更新しました')
    } catch {
      toast.error('更新に失敗しました')
    }
  }

  // 質問の削除
  const handleDeleteQuestion = async (questionId: string) => {
    try {
      await deleteQuestion.mutateAsync({ lessonId, questionId })
      toast.success('質問を削除しました')
    } catch {
      toast.error('削除に失敗しました')
    }
  }

  // 回答の編集
  const handleStartEditAnswer = (answerId: string, currentText: string) => {
    setEditingAnswerId(answerId)
    setEditingAnswerText(currentText)
  }

  const handleSaveEditAnswer = async (questionId: string, answerId: string) => {
    if (!editingAnswerText.trim()) return
    try {
      await updateAnswer.mutateAsync({
        lessonId,
        questionId,
        answerId,
        data: { answer: editingAnswerText.trim() },
      })
      setEditingAnswerId(null)
      toast.success('回答を更新しました')
    } catch {
      toast.error('更新に失敗しました')
    }
  }

  // 回答の削除
  const handleDeleteAnswer = async (questionId: string, answerId: string) => {
    try {
      await deleteAnswer.mutateAsync({ lessonId, questionId, answerId })
      toast.success('回答を削除しました')
    } catch {
      toast.error('削除に失敗しました')
    }
  }

  const isOwner = (userId: string) => user?.id === userId
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin'

  if (isLoading) return <Skeleton className="h-48" />

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Q&A ({questions.length}件)</h3>
        <Button size="sm" onClick={() => setShowForm(!showForm)}>
          <MessageSquare className="mr-2 h-4 w-4" />
          質問する
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardContent className="space-y-3 pt-4">
            <Textarea
              placeholder="質問を入力..."
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              rows={3}
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSubmitQuestion} disabled={createQuestion.isPending}>
                投稿
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>
                キャンセル
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {questions.length === 0 ? (
        <p className="text-sm text-muted-foreground">まだ質問がありません。</p>
      ) : (
        <div className="space-y-3">
          {questions.map((q: any) => (
            <Card key={q.question.id}>
              <CardContent className="pt-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {editingQuestionId === q.question.id ? (
                      <div className="space-y-2">
                        <Textarea
                          value={editingQuestionText}
                          onChange={(e) => setEditingQuestionText(e.target.value)}
                          rows={2}
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleSaveEditQuestion(q.question.id)}
                            disabled={updateQuestion.isPending}
                          >
                            保存
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingQuestionId(null)}
                          >
                            <X className="mr-1 h-3 w-3" />
                            キャンセル
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm">{q.question.question}</p>
                        <div className="mt-1 flex items-center gap-2">
                          <p className="text-xs text-muted-foreground">
                            {q.userName} •{' '}
                            {new Date(q.question.createdAt).toLocaleDateString('ja-JP')}
                          </p>
                          {isOwner(q.question.userId) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-1"
                              onClick={() =>
                                handleStartEditQuestion(q.question.id, q.question.question)
                              }
                            >
                              <Edit2 className="h-3 w-3" />
                            </Button>
                          )}
                          {(isOwner(q.question.userId) || isAdmin) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-1 text-destructive hover:text-destructive"
                              onClick={() => handleDeleteQuestion(q.question.id)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleUpvote(q.question.id)}
                    className="shrink-0"
                  >
                    <ThumbsUp className="mr-1 h-4 w-4" />
                    {q.question.upvotes ?? 0}
                  </Button>
                </div>

                {/* Answers */}
                {q.answers?.map((a: any) => (
                  <div key={a.answer.id} className="ml-4 mt-3 border-l-2 pl-3">
                    {editingAnswerId === a.answer.id ? (
                      <div className="space-y-2">
                        <Textarea
                          value={editingAnswerText}
                          onChange={(e) => setEditingAnswerText(e.target.value)}
                          rows={2}
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleSaveEditAnswer(q.question.id, a.answer.id)}
                            disabled={updateAnswer.isPending}
                          >
                            保存
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingAnswerId(null)}
                          >
                            <X className="mr-1 h-3 w-3" />
                            キャンセル
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm">{a.answer.answer}</p>
                        <div className="mt-1 flex items-center gap-2">
                          <p className="text-xs text-muted-foreground">
                            {a.userName} •{' '}
                            {new Date(a.answer.createdAt).toLocaleDateString('ja-JP')}
                          </p>
                          {isOwner(a.answer.userId) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-1"
                              onClick={() => handleStartEditAnswer(a.answer.id, a.answer.answer)}
                            >
                              <Edit2 className="h-3 w-3" />
                            </Button>
                          )}
                          {(isOwner(a.answer.userId) || isAdmin) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-1 text-destructive hover:text-destructive"
                              onClick={() => handleDeleteAnswer(q.question.id, a.answer.id)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}

                {/* Answer input */}
                <div className="mt-3 flex gap-2">
                  <Input
                    placeholder="回答を入力..."
                    value={answerText[q.question.id] || ''}
                    onChange={(e) =>
                      setAnswerText((prev) => ({ ...prev, [q.question.id]: e.target.value }))
                    }
                    className="text-sm"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleSubmitAnswer(q.question.id)}
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
